/**
 * Billing Configuration
 * Centralized configuration for billing, payment methods, and plan details
 */

// Plan Definitions - Credit Packages Only (No Subscriptions)
// Updated per AL-265 (Jul 2026): Starter/Growth/Enterprise tiers with clean per-credit rates
export const BILLING_PLANS = {
  STARTER: {
    id: 'starter',
    name: 'Starter',
    description: '100 credits · $50/ea',
    type: 'credits',
    credits: 100,
    price: 500000, // $5,000 = 500000 cents
    originalPrice: 500000,
    pricePerCredit: 5000, // $50 per credit
    originalPricePerCredit: 5000,
    discountPercent: 0,
    savings: 0,
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
    ],
  },
  GROWTH: {
    id: 'growth',
    name: 'Growth',
    description: '222 credits · ~$45/ea',
    type: 'credits',
    credits: 222,
    price: 1000000, // $10,000 = 1000000 cents (per AL-265 ticket headline price)
    originalPrice: 1110000, // $11,100 at $50/credit rate
    pricePerCredit: 4505, // $45.05 per credit ($10,000 / 222, rounded)
    originalPricePerCredit: 5000, // $50 per credit
    discountPercent: 10, // (50 - 45.045) / 50 = 9.9% -> rounded to 10%
    savings: 110000, // Save $1,100
    badge: 'POPULAR',
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
      'Priority Support',
    ],
  },
  ENTERPRISE: {
    id: 'enterprise',
    name: 'Enterprise',
    description: '625 credits · $40/ea',
    type: 'credits',
    credits: 625,
    price: 2500000, // $25,000 = 2500000 cents (625 x $40 exactly)
    originalPrice: 3125000, // $31,250 at $50/credit rate
    pricePerCredit: 4000, // $40 per credit
    originalPricePerCredit: 5000, // $50 per credit
    discountPercent: 20,
    savings: 625000, // Save $6,250
    badge: 'BEST VALUE',
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
      'Priority Support'
    ],
  },
  /**
   * @deprecated Retired per AL-265 (Jul 2026). Kept for backward compatibility with
   * historical payments/records that reference this plan — NOT shown in the plan
   * selector UI (see StripeBilling.tsx PLAN_CONFIGS). Do not offer for new purchases.
   */
  OFFER: {
    id: 'offer',
    name: 'Offer',
    description: '1,667 credits · $30/ea',
    type: 'credits',
    credits: 1667,
    price: 5000000, // $50,000 = 5000000 cents
    originalPrice: 8335000, // $83,350 at $50/credit rate
    pricePerCredit: 3000, // $30 per credit
    originalPricePerCredit: 5000, // $50 per credit
    discountPercent: 40,
    savings: 3335000, // Save $33,350
    badge: 'LIMITED',
    features: [
      'Know Your Customer compliance',
      'Customer risk assessment',
      'AUSTRAC SMR Reporting',
      '7+ Year Record Keeping',
      'Priority Support',
      'Volume Discount - Lowest Rate',
    ],
  },
} as const;

// Billing Cycles
export const BILLING_CYCLES = {
  MONTHLY: {
    id: 'monthly',
    label: 'Monthly',
    discount: 0,
    highlighted: false,
  },
  ANNUAL: {
    id: 'annual',
    label: 'Annual',
    discount: 0,
    highlighted: true,
    badge: 'RECOMMENDED',
    badgeColor: '#28a745',
    description: 'Get your credits annually and save more',
    backgroundColor: '#e8f5e9',
    borderColor: '#4caf50',
  },
} as const;

// Installment Options (for Annual billing)
export const INSTALLMENT_OPTIONS = {
  FULL: {
    id: 'full',
    label: 'Pay in Full',
    payments: 1,
    installments: [],
  },
} as const;

// Payment Methods Configuration
export const PAYMENT_METHODS = {
  CARD: {
    id: 'card',
    name: 'Card Payment',
    icon: '💳',
    color: '#4285F4',
    backgroundColor: '#E3F2FD',
    borderColor: '#BBDEFB',
  },
  BANK: {
    id: 'bank',
    name: 'Bank Transfer',
    icon: '🏦',
    color: '#2E7D32',
    backgroundColor: '#E8F5E9',
    borderColor: '#C8E6C9',
  },
  BNPL: {
    id: 'bnpl',
    name: 'Buy Now, Pay Later',
    icon: '📅',
    color: '#D32F2F',
    backgroundColor: '#FFEBEE',
    borderColor: '#EF9A9A',
  },
} as const;

// Card Types for Card Payment
export const CARD_TYPES = [
  {
    name: 'Visa',
    label: 'Visa',
    type: 'svg',
    svgPath: 'M651.185.5c-70.933 0-134.322 36.766-134.322 104.694 0 77.9 112.423 83.28 112.423 122.415 0 16.478-18.884 31.229-51.137 31.229-45.773 0-79.984-20.611-79.984-20.611l-14.638 68.547s39.41 17.41 91.734 17.41c77.552 0 138.576-38.572 138.576-107.66 0-82.316-112.89-87.537-112.89-123.86 0-12.91 15.501-27.053 47.662-27.053 36.286 0 65.892 14.99 65.892 14.99l14.326-66.204S696.614.5 651.185.5zM2.218 5.497L.5 15.49s29.842 5.461 56.719 16.356c34.606 12.492 37.072 19.765 42.9 42.353l63.51 244.832h85.138L379.927 5.497h-84.942L210.707 218.67l-34.39-180.696c-3.154-20.68-19.13-32.477-38.685-32.477H2.218zm411.865 0L347.449 319.03h80.999l66.4-313.534h-80.765zm451.759 0c-19.532 0-29.88 10.457-37.474 28.73L709.699 319.03h84.942l16.434-47.468h103.483l9.994 47.468H999.5L934.115 5.497h-68.273zm11.047 84.707l25.178 117.653h-67.454z',
    svgViewBox: '0.5 0.5 999 323.684',
    color: '#1434cb',
    width: 48,
    height: 32,
  },
  {
    name: 'Mastercard',
    label: 'Mastercard',
    type: 'svg',
    svgPath: 'm519.7 182.2c0 79.5-64.3 143.9-143.6 143.9s-143.6-64.4-143.6-143.9 64.2-143.9 143.5-143.9 143.7 64.4 143.7 143.9z M342.4 182.2c0 79.5-64.3 143.9-143.6 143.9s-143.6-64.4-143.6-143.9 64.3-143.9 143.6-143.9 143.6 64.4 143.6 143.9z M287.4 68.9c-33.5 26.3-55 67.3-55 113.3s21.5 87 55 113.3c33.5-26.3 55-67.3 55-113.3s-21.5-86.9-55-113.3z',
    svgViewBox: '55.2 38.3 464.5 287.8',
    colors: ['#f79f1a', '#ea001b', '#ff5f01'],
    width: 48,
    height: 32,
  },
  {
    name: 'American Express',
    label: 'American Express',
    type: 'svg',
    svgPath: 'M230.1 5.8h-3.6c-.8 0-1.3-.5-1.3-1.2s.6-1.1 1.3-1.1h6.5l1.5-3.3h-8c-3.3 0-5.2 2-5.2 4.6 0 2.8 1.8 4.4 4.7 4.4h3.6c.8 0 1.3.5 1.3 1.2s-.5 1.2-1.3 1.2h-7.8v3.3h7.8c3.3 0 5.2-1.9 5.2-4.7 0-2.8-1.7-4.4-4.7-4.4m-14.3 0h-3.6c-.8 0-1.3-.5-1.3-1.2s.6-1.1 1.3-1.1h6.5l1.5-3.3h-8c-3.3 0-5.2 2-5.2 4.6 0 2.8 1.8 4.4 4.7 4.4h3.6c.8 0 1.3.5 1.3 1.2s-.5 1.2-1.3 1.2h-7.8v3.3h7.8c3.3 0 5.2-1.9 5.2-4.7 0-2.8-1.7-4.4-4.7-4.4m-22.8 9h12.3v-3.3h-8.4V9.1h8.2V5.8h-8.2V3.5h8.4V.2H193zm-7.7-8.4h-4.1V3.5h4.1c1.2 0 1.8.7 1.8 1.5-.1.8-.7 1.4-1.8 1.4m5.7-1.5c0-2.8-2-4.7-5.2-4.7h-8.4v14.6h3.9V9.7h1.4l4.4 5.1h4.8l-4.8-5.3c2.4-.5 3.9-2.3 3.9-4.6m-21 1.8h-4.2V3.5h4.2c1.2 0 1.8.7 1.8 1.6-.1.9-.7 1.6-1.8 1.6m.4-6.5h-8.5v14.6h3.9V10h4.5c3.3 0 5.3-2.1 5.3-4.9 0-2.9-2-4.9-5.2-4.9m-9.2 0h-5l-3.8 4.4-3.8-4.4h-5.1l6.4 7.2-6.5 7.4h5l3.9-4.6 4 4.6h5.1l-6.6-7.5zm-30.7 14.6h12.3v-3.3h-8.4V9.1h8.2V5.8h-8.2V3.5h8.4V.2h-12.3zm-13.7-5.7l-6-8.9h-4.9v14.6h3.9V5.5l6.1 9.3h4.7V.2h-3.8zm-22.7-.5l2-4.9 2 4.9h-4zM93.7.2l-6.4 14.6h4.3l1.2-3h6.8l1.3 3h4.4L98.7.2h-5zM80.9 7.6v-.3c0-2.2 1.2-3.7 3.7-3.7H89V.2h-4.7C79.5.2 77 3.3 77 7.3v.3c0 4.5 2.8 7.1 7.1 7.1h1.3l1.6-3.3h-2.3c-2.3.1-3.8-1.2-3.8-3.8M71.1.2v14.6H75V.2zm-7.8 6.2h-4.1V3.5h4.1c1.2 0 1.8.7 1.8 1.5s-.6 1.4-1.8 1.4M69 4.9c0-2.8-2-4.7-5.2-4.7h-8.4v14.6h3.9V9.7h1.4l4.4 5.1H70l-4.8-5.3C67.6 9 69 7.2 69 4.9m-28.5 9.9h12.3v-3.3h-8.4V9.1h8.2V5.8h-8.2V3.5h8.4V.2H40.5zM28.1 9.4L24.8.2h-6.1v14.6h3.8V4.3l3.8 10.5h3.5l3.9-10.5v10.5h3.8V.2h-6.1zM6.9 8.6l2-4.9 2 4.9h-4zM6.4.2L0 14.8h4.3l1.2-3h6.8l1.3 3H18L11.5.2H6.4z',
    svgViewBox: '0 0 235 15',
    color: '#006fcf',
    width: 48,
    height: 32,
  },
] as const;

// BNPL Providers
export const BNPL_PROVIDERS = {
  AFFIRM: {
    id: 'affirm',
    name: 'Affirm',
    icon: '💰',
    description: '4 interest-free payments',
    color: '#0066FF',
  },
  KLARNA: {
    id: 'klarna',
    name: 'Klarna',
    icon: '✨',
    description: 'Pay in 4 interest-free installments',
    color: '#FFB3D9',
  },
  AFTERPAY: {
    id: 'afterpay',
    name: 'Afterpay',
    icon: '📦',
    description: '4 fortnightly payments',
    color: '#B1E8D8',
  },
  PAYPAL: {
    id: 'paypal',
    name: 'PayPal',
    icon: '🅿️',
    description: 'Monthly installments',
    color: '#003087',
  },
} as const;

// Bank Transfer Details
export const BANK_TRANSFER_DETAILS = {
  bankName: 'NAB (National Australia Bank)',
  accountNumber: '87-504-3783',
  bsbCode: '082-356',
  instructions: 'Please include your reference number in the payment details. Your invoice will be sent to your email.',
} as const;

// Card Payment Configuration
export const CARD_PAYMENT_CONFIG = {
  title: 'Accepted Card Types',
  description: 'We accept the following card types for secure payment:',
  pciMessage: 'All transactions are secured with PCI DSS Level 1 compliance. Your card details are encrypted and never stored on our servers.',
  billingEmailPlaceholder: 'billing@example.com',
} as const;

// Apple Pay Configuration
export const APPLE_PAY_CONFIG = {
  title: 'Apple Pay',
  description: 'Fast and secure payment with:',
  features: [
    'One-click checkout',
    'Touch ID / Face ID authentication',
    'Secure tokenization',
  ],
  message: 'Your payment details are encrypted and secure.',
} as const;

// Google Pay Configuration
export const GOOGLE_PAY_CONFIG = {
  title: 'Google Pay',
  description: 'Fast and secure payment with:',
  features: [
    'Quick checkout',
    'Biometric authentication',
    'Encrypted transactions',
  ],
  message: 'Your payment information is kept secure.',
} as const;

// Apple Pay SVG Configuration
export const APPLE_PAY_SVG_VIEWBOX = '-2.48 135.79 499.389 229.888';

// Google Pay SVG Configuration
export const GOOGLE_PAY_SVG_VIEWBOX = '0 0 2065.9 2064.8';

// Icon Sizes
export const ICON_SIZES = {
  SMALL: { width: '32px', height: '32px' },
  MEDIUM: { width: '48px', height: '48px' },
  LARGE: { width: '60px', height: '60px' },
} as const;

// Pricing Display Configuration
export const PRICING_CONFIG = {
  showAnnualDiscount: false,
  discountPercentage: 0,
  currencySymbol: '$',
  currencyCode: 'AUD',
} as const;
